import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { NgTemplateOutlet } from '@angular/common';

import { TemplatePortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  Input,
  OnDestroy,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef
} from '@angular/core';
import {
  ColorUtils,
  ContextMenuComponent,
  ContextSubmenuComponent,
  ContrastTextDirective,
  IContextMenuOption
} from '@cinetixx/cinetixx-ui';
import { AuditoriumShapeDirective } from '../../core/directives/auditorium-shape.directive';
import { DragStates, SeatStatuses, ShapeSeatTypes, ShapeTypes } from '../../core/enums';
import { IRectPoint, IShape } from '../../core/interfaces';
import { LoveSeatManager, RowManager, SeatManager, ShapeAttributesManager, ShapeManager } from '../../core/managers';
import { CreatorActionsService, CreatorStateService, ResizeService } from '../../core/services/app-services';
import { FlattenSeat, GuidWrapper } from '../../core/types';
import { BLOCKED_SEAT_COLOR } from '../../core/constants';
import { CX_CORE_STANDALONE } from '../../../shared/cx-core-standalone';
import { PointsToPolygonPipe } from '../../core/pipes';
import { FormatTextPipe } from '../../core/pipes';
import { SvgToBase64BgPipe } from '../../core/pipes/svg-to-base64-bg.pipe';
@Component({
  imports: [ ...CX_CORE_STANDALONE, ContextMenuComponent, ContextSubmenuComponent, PointsToPolygonPipe, FormatTextPipe, SvgToBase64BgPipe, NgTemplateOutlet ],
  selector: 'app-shape',
  templateUrl: './shape.component.html',
  styleUrls: ['./shape.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'shape draggable'
  },
  providers: [
    ContrastTextDirective,
  ],
  hostDirectives: [
    {
      directive: AuditoriumShapeDirective,
      inputs: ['componentRef']
    }
  ]
})
export class ShapeComponent implements AfterViewInit, OnDestroy {

  @ViewChild('shapeTooltip') shapeTooltipTemplateRef!: TemplateRef<HTMLDivElement>;
  @ViewChild('addToTheRowSubmenu') contextSubmenuComponent: ContextSubmenuComponent;
  @ViewChild(ContextMenuComponent) contextMenuComponent: ContextMenuComponent;
  @ViewChildren('textContainer') textContainers: QueryList<ElementRef<HTMLElement>>;

  @Input() set shape(value: IShape) {
    if (value) {
      this._shape = value;
      this.textRectPoint = this._shape.rectPoints?.find(x => x.rectNumber === 0) ?? null;
      this.infoText1RectPoint = this._shape.rectPoints?.find(x => x.rectNumber === 1) ?? null;
      this.infoText2RectPoint = this._shape.rectPoints?.find(x => x.rectNumber === 2) ?? null;
      this.backgroundColor = this._shape?.color || '';

      if (this.isForNewCashApp) {
        if (this._shape.shapeSeatType !== ShapeSeatTypes.NONE) {
          this._elementRef.nativeElement.classList.add(
            'shape--seat',
            `shape--seat--type-${ this._shape.loveSeatId && this._shape.shapeSeatType === ShapeSeatTypes.SEAT ? 'LoveSeatInner' : this._shape.shapeSeatType }`
          );
        } else {
          this._elementRef.nativeElement.classList.add(
            'shape--element',
            `shape--element--type-${this._shape.shapeElementType}`
          );
        }
      }

      if (this._shape.id) {
        this.id = this._shape.id;
      }

      if (this._shape?.image) {
        this._renderer2.setAttribute(this._elementRef.nativeElement, 'data-image', `${this._shape.image}`);
      }

      if (this._shape?.shapeType) {
        ShapeAttributesManager.setShapeType(this._elementRef.nativeElement, this._shape.shapeType);
      }
    }
  };

  @Input() set dataObject(value: GuidWrapper<FlattenSeat>) {
    /**
     * if value has sector element than it is seat otherwise it is base element
     */
    this._dataObject = value;

    if (value.webX !== undefined && value.webX !== null) {
      this.x = `${value.webX}px`;
    }

    if (value.webY !== undefined && value.webY !== null) {
      this.y = `${value.webY}px`;
    }

    if (value.w !== undefined && value.w !== null) {
      this.width = `${value.w}px`;
    }

    if (value.h !== undefined && value.h !== null) {
      this.height = `${value.h}px`;
    }

    if (value.color || value.priceAreaColor) {
      this.backgroundColor = value.priceAreaColor ?? value.color;
    }

    this._cdr.markForCheck();
  }
  @Input() showContextMenu = true;
  @Input() @HostBinding('attr.data-shape-has-error') hasError = false;
  @Input() canBeHovered = false;
  @HostBinding('attr.data-has-resize-points-enabled') @Input() shouldCreateResizePoints = true;
  @HostBinding('attr.data-background-color') @Input() set backgroundColor(value: string) {
    this._backgroundColor = value;
    this._contrastTextDirective.backgroundColor = this._backgroundColor;
  };
  @HostBinding('style.position') @Input() position: string;
  @HostBinding('attr.draggable') @Input() draggable = false;
  @HostBinding('style.left') @Input() x: string;
  @HostBinding('style.top') @Input() y: string;
  @HostBinding('style.width') @Input() width: string;
  @HostBinding('style.height') @Input() height: string;
  @HostBinding('attr.id') @Input() id: number;
  @HostBinding('attr.data-drag-id') @Input() dragId: string;

  public set isTooltipVisible(value: boolean) {
    if (value && this._elementRef?.nativeElement?.isConnected) {
      const rect = this._elementRef.nativeElement.getBoundingClientRect();

      this._overlayRef = this._overlay.create({
        positionStrategy: this._overlay.position()
          .flexibleConnectedTo({
            x: rect.x,
            y: rect.y,
            width: rect.width,
            height: rect.height
          })
          .withPositions([{
            originX: 'center',
            originY: 'top',
            overlayX: 'center',
            overlayY: 'bottom'
          }])
      });

      this._overlayRef.attach(new TemplatePortal(this.shapeTooltipTemplateRef, this._viewContainerRef));
    } else {
      this._overlayRef?.detach();
    }

    this._cdr.detectChanges();
  };
  public textRectPoint: IRectPoint;
  public infoText1RectPoint: IRectPoint;
  public infoText2RectPoint: IRectPoint;
  public shapeOptions: IContextMenuOption[] = [];
  public shapeSubmenuOptions: IContextMenuOption[] = [];
  public readonly auditoriumShapeDirective = inject(AuditoriumShapeDirective, { host: true });
  public readonly seatStatuses = SeatStatuses;
  public readonly blockedSeatColor = BLOCKED_SEAT_COLOR;

  private _overlayRef: OverlayRef;
  private _timeout$: NodeJS.Timeout;
  private _resizeService: ResizeService;
  private _shape: IShape;
  private _dataObject: GuidWrapper<FlattenSeat>;
  private _backgroundColor: string;
  private _resizeObserver: ResizeObserver = new ResizeObserver(() => {
    const textContainers = Array.from(this._elementRef.nativeElement.querySelectorAll<HTMLElement>('.text-container'));

    if (textContainers?.length) {
      for (const textContainer of textContainers) {
        textContainer.style.setProperty('--container-width', `${textContainer.offsetWidth || 36}px`);
        textContainer.style.setProperty('--container-height', `${textContainer.offsetHeight || 36}px`);
      }
    }
  });

  public constructor(
    private readonly _elementRef: ElementRef<HTMLElement>,
    private readonly _renderer2: Renderer2,
    private readonly _contrastTextDirective: ContrastTextDirective,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _destroyRef: DestroyRef,
    private readonly _overlay: Overlay,
    private readonly _viewContainerRef: ViewContainerRef
  ) {
    this._resizeService = new ResizeService(this._destroyRef);

    if (this.isForNewCashApp) {
      this._elementRef.nativeElement.classList.add('shape--new');
    }
  }

  public ngAfterViewInit(): void {
    if (this.textContainers?.length) {
      this._resizeObserver.observe(Array.from(this.textContainers)[0].nativeElement);
    }

    if (
      this.shouldCreateResizePoints &&
      !this._dataObject?.loveSeatId &&
      (
        !CreatorStateService.seatsPlan.isForNewCashApp ||
        (CreatorStateService.seatsPlan.isForNewCashApp && !this._dataObject?.seatTypeId)
      )
    ) {
      this._resizeService.createResizePoints(this._elementRef.nativeElement);
    }
  }

  public onmouseenter(): void {
    if (this._dataObject.ruleTipText) {
      this._timeout$ = setTimeout(() => {
        this.isTooltipVisible = true;
        this._cdr.detectChanges();
      }, 500);
    }

    if ((this._dataObject.priceAreaColor ?? this._dataObject.color) && this.canBeHovered) {
      this.backgroundColor = ColorUtils.getContrastColor(this._dataObject.priceAreaColor ?? this._dataObject.color, 0.3);
    }
  }

  public get isForNewCashApp(): boolean {
    return CreatorStateService.seatsPlan.isForNewCashApp;
  }

  public onmouseleave(): void {
    if (this._timeout$) {
      clearTimeout(this._timeout$);
    }

    if (this.canBeHovered) {
      this.backgroundColor = this._dataObject.priceAreaColor ?? this._dataObject.color;
    }

    this.isTooltipVisible = false;
  }

  public get dataObject(): GuidWrapper<FlattenSeat> {
    return this._dataObject;
  }

  public get shape(): IShape {
    return this._shape;
  }

  public get backgroundColor(): string {
    return this._backgroundColor;
  }

  public open(event: MouseEvent): void {
    if (CreatorStateService.currentStep !== 'seats-plan' && CreatorStateService.currentStep !== 'sector-plan') {
      return;
    }

    let loveSeats: HTMLElement[] = [];

    if (ShapeAttributesManager.getShapeType(this._elementRef.nativeElement) === ShapeTypes.PLACE_SHAPE) {
      const seat = SeatManager.getSeat(this._dataObject.guid);

      if (seat.loveSeatId) {
        loveSeats = LoveSeatManager.getAllLoveSeatsElementsByLoveSeatId(seat.loveSeatId);

        for (const loveSeat of loveSeats) {
          ShapeAttributesManager.setDragState(loveSeat, DragStates.SELECTED);
        }

        setTimeout(() => LoveSeatManager.createSelection(seat.loveSeatId, false));
      } else {
        ShapeAttributesManager.setDragState(this._elementRef.nativeElement, DragStates.SELECTED);
      }
    } else {
      ShapeAttributesManager.setDragState(this._elementRef.nativeElement, DragStates.SELECTED);
    }

    const rankTokens = RowManager.getAllRanksWithTokens();

    this.shapeOptions = [
      {
        title: '<i class="ri-file-copy-2-line m-r-8"></i>' + 'Copy'.trans('auditoriums/shape/context-menu/copy/option.Text'),
        action: () => CreatorStateService.shapesToCopy = loveSeats?.length ? loveSeats : [this._elementRef.nativeElement]
      },
      {
        title: '<i class="ri-unpin-line m-r-8"></i>' + 'Remove from row'.trans('auditoriums/shape/context-menu/remove-from-row/option.Text'),
        isHidden: !this._dataObject.rank,
        action: () => {
          if (loveSeats?.length) {
            CreatorActionsService.removeSeatsFromRow(loveSeats.map(x => SeatManager.getSeat(ShapeAttributesManager.getGUID(x))), { emitEvent: true });
          } else {
            CreatorActionsService.removeSeatsFromRow([SeatManager.getSeat(this._dataObject.guid)], {emitEvent: true});
          }
        }
      },
      {
        title: '<i class="ri-pushpin-line m-r-8"></i>' + 'Add to the row'.trans('auditoriums/shape/context-menu/add-to-the-row/option.Text') + '<i class="ri-arrow-right-s-line m-l-8"></i>',
        submenu: this.contextSubmenuComponent,
        isHidden: !!this._dataObject.rank || rankTokens?.length === 0 || ShapeAttributesManager.getShapeType(this._elementRef.nativeElement) !== ShapeTypes.PLACE_SHAPE
      },
      {
        title: '<i class="ri-delete-bin-line m-r-8 c-t-nok-400"></i>' + `<span class="c-t-nok-400">${'Delete element'.trans('auditoriums/shape/context-menu/delete/option.Text')}</span>`,
        action: () => {
          if (ShapeAttributesManager.getShapeType(this._elementRef.nativeElement) === ShapeTypes.PLACE_SHAPE) {
            CreatorActionsService.deleteSeats(loveSeats?.length ? loveSeats : [this._elementRef.nativeElement], {emitEvent: true});
          } else {
            CreatorActionsService.deleteBaseElements([this._elementRef.nativeElement], {emitEvent: true})
          }
        }
      },
      {
        title: '<i class="ri-delete-bin-line m-r-8 c-t-nok-400"></i>' + `<span class="c-t-nok-400">${'Delete from love seat'.trans('auditoriums/shape/context-menu/delete-from-love-seat/option.Text')}</span>`,
        isHidden: !(this._dataObject.loveSeatId && this.shape.shapeSeatType === ShapeSeatTypes.SEAT),
        action: () => {
          CreatorActionsService.removeSeatFromLoveSeat([this._elementRef.nativeElement], {emitEvent: true});
        }
      }
    ];

    this.shapeSubmenuOptions = rankTokens.sort((a, b) => a.rank - b.rank).map(data => ({
      title: data.rankToken,
      action: () => {
        if (loveSeats?.length) {
          CreatorActionsService.addSeatsToRow(
            loveSeats.map(x => SeatManager.getSeat(ShapeAttributesManager.getGUID(x))),
            data.rank,
            data.rankToken,
            { emitEvent: true }
          );
        } else {
          CreatorActionsService.addSeatsToRow(
            [CreatorStateService.seatsPlan.seats.find(x => x.guid === this._dataObject.guid)],
            data.rank,
            data.rankToken,
            { emitEvent: true }
          );
        }

        const rowSeats = RowManager.getRowSeats(data.rankToken);

        for (const rowSeat of rowSeats) {
          const seatElement = SeatManager.getHTMLElement(rowSeat.guid);
          const isNewAddedSeat = seatElement === this._elementRef.nativeElement;

          ShapeManager.renewElement$(
            seatElement,
            rowSeat,
            { position: 'absolute', showContextMenu: true }
          ).subscribe((renewedEl) => {
            if (isNewAddedSeat && !loveSeats?.length) {
              ShapeAttributesManager.setDragState(renewedEl, DragStates.SELECTED);
            }
          });
        }

        RowManager.updateRowLine(data.rankToken);
      }
    }));

    this._cdr.detectChanges();
    this.contextMenuComponent.ngxContextMenu.open(event);
  }

  public ngOnDestroy(): void {
    this._resizeObserver?.disconnect();
  }
}
